import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { getOrderConsumptions, retryOrderConsumption } from '../services/api';
import { describeConsumptionError, issueTargets } from '../utils/consumptionIssues';
import HelpIcon from './help/HelpIcon';
import './ConsumptionIssuesPanel.css';

/**
 * Panel de "consumos con problemas" (B6 / RES-34).
 *
 * Lista los pedidos cuyo descuento de stock quedó incompleto, con el detalle de qué falló y un
 * acceso directo al producto o al insumo a corregir. Una vez corregido, "Reintentar" reprocesa:
 * el backend recalcula el consumo y aplica solo la diferencia, así que no descuenta dos veces.
 *
 * @param {Object} props - Props
 * @param {Function} [props.onCountChange] - Recibe la cantidad de consumos con problemas
 * @returns {JSX.Element} Panel
 */
function ConsumptionIssuesPanel({ onCountChange }) {
  const { t } = useTranslation();
  const [consumptions, setConsumptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOrderConsumptions({ status: 'partial', limit: 50 });
      const items = data.items || [];
      setConsumptions(items);
      if (onCountChange) onCountChange(items.length);
    } catch {
      setConsumptions([]);
      if (onCountChange) onCountChange(0);
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRetry = async (orderId) => {
    setRetrying(orderId);
    setNotice('');
    try {
      const result = await retryOrderConsumption(orderId);
      setNotice(
        result?.status === 'applied'
          ? t('inventory.consumptionIssues.retryFixed')
          : t('inventory.consumptionIssues.retryStillPartial')
      );
      await load();
    } catch (error) {
      setNotice(error?.response?.data?.message || t('inventory.consumptionIssues.retryError'));
    } finally {
      setRetrying(null);
    }
  };

  return (
    <div className="card consumption-issues">
      <div className="section-header">
        <AlertTriangle size={18} aria-hidden="true" />
        <h2>
          {t('inventory.consumptionIssues.title')}
          <HelpIcon topicKey="inventory.consumptionIssues" />
        </h2>
        {consumptions.length > 0 && (
          <span className="consumption-issues-count">{consumptions.length}</span>
        )}
      </div>

      <p className="text-muted small">{t('inventory.consumptionIssues.help')}</p>

      {notice && (
        <p className="consumption-issues-notice" role="status">
          {notice}
        </p>
      )}

      {loading && <p className="text-muted small">{t('common.loading')}</p>}

      {!loading && consumptions.length === 0 && (
        <p className="text-muted small">{t('inventory.consumptionIssues.empty')}</p>
      )}

      {!loading &&
        consumptions.map((consumption) => (
          <div key={consumption.orderId} className="consumption-issue">
            <div className="consumption-issue-head">
              <span className="consumption-issue-order">
                {t('inventory.consumptionIssues.order')} #{consumption.orderId.slice(-6)}
              </span>
              {consumption.retryCount > 0 && (
                <span className="text-muted small">
                  {t('inventory.consumptionIssues.retries', { count: consumption.retryCount })}
                </span>
              )}
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => handleRetry(consumption.orderId)}
                disabled={retrying === consumption.orderId}
              >
                <RefreshCw size={14} aria-hidden="true" />
                {retrying === consumption.orderId
                  ? t('inventory.consumptionIssues.retrying')
                  : t('inventory.consumptionIssues.retry')}
              </button>
            </div>

            <ul className="consumption-issue-errors">
              {(consumption.errors || []).map((error, index) => (
                <li key={index}>
                  <span>{describeConsumptionError(error, t)}</span>
                  <span className="consumption-issue-links">
                    {issueTargets(error).map((target) => (
                      <Link key={target.to} to={target.to}>
                        {t(target.labelKey)}
                      </Link>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </div>
  );
}

export default ConsumptionIssuesPanel;
